/**
 * A util method for producing a query string with possibly multiple queries.
 *
 * Example:
 * queryStringFromObject({a: alpha, b: beta}) returns '?a=alpha&b=beta'.
 */
type Primitive = string | boolean | number;

export type QueryParameters = Readonly<
    Record<
        string,
        Primitive | null | undefined | Array<Primitive | null | undefined>
    >
>;

// NOTE(slim): webapp REST API convention encodes bools as "1" or "0".
const stringifyBool = (b: boolean) => (b ? "1" : "0");

// Converts a value to its string representation.
// Using the `String()` constructor preserves the specified behavior of encodeURIComponent.
const stringify = (value?: Primitive | null) =>
    typeof value === "boolean" ? stringifyBool(value) : String(value);

// Formats an individual primitive value.
const formatPair = (key: string, value?: Primitive | null) =>
    `${key}=${encodeURIComponent(stringify(value))}`;

/**
 * Format an object as query parameters.
 *
 * @params data The object to format as query parameters.
 * @params includeQuestionMark Whether to include the question mark at the
 * beginning of the query string.
 *
 * @deprecated Use `URLSearchParams`:
 * ```ts
 * const params = new URLSearchParams(data);
 * const queryStringWithoutQuestionMark = params.toString();
 * const queryStringWithQuestionMark = `?${params.toString()}`;
 * ```
 */
function formatQueryString(
    data: QueryParameters,
    includeQuestionMark = false,
): string {
    const queries = Object.keys(data);

    if (!queries.length) {
        return "";
    }
    return (
        (includeQuestionMark ? "?" : "") +
        queries
            .map((key) =>
                Array.isArray(data[key])
                    ? data[key].map((query) => formatPair(key, query)).join("&")
                    : formatPair(key, data[key]),
            )
            .sort() // Not necessary, but improves cache performance
            .join("&")
    );
}

export default formatQueryString;
